import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  formatPoints, formatRating, resolveConfig, streakOn, type ChallengeConfig,
  interacaoDe,
} from "@nhomi/core";
import { watchChallenge, type Challenge as ChallengeDoc } from "@/data/challenges";
import { watchMembers, standings, type Member } from "@/data/members";
import { dayKey, plateDate, watchFeed, type Plate } from "@/data/plates";
import { reagir, watchMyReaction } from "@/data/social";
import { useSession } from "@/lib/auth";
import { useBloqueados, semBloqueados } from "@/lib/bloqueios";
import { useToqueLongo } from "@/lib/toque";
import { Anuncio } from "@/ui/Anuncio";
import { ValeFalta } from "@/ui/ValeFalta";
import { CartaoDoCampeao, useTemporadaVigente } from "@/ui/Campeao";
import { Cutucar } from "@/ui/Cutucar";
import { ChegouOResumo } from "@/ui/Resumos";
import { FaixaDeMissoes } from "@/ui/Missoes";
import { BarraDeReacao, ResumoDeReacoes } from "@/ui/Reacoes";
import { Avatar, Empty, Spinner } from "@/ui/primitives";
import { EsqueletoDoFeed } from "@/ui/Esqueleto";
import { StoragePhoto } from "@/ui/StoragePhoto";
import { TabBar } from "@/ui/TabBar";
import {
  IconBack, IconBolt, IconChat, IconChevron, IconFork, IconPlus, IconStar,
} from "@/ui/icons";

/**
 * Um card do feed.
 *
 * É componente próprio porque o toque longo é um hook, e hook não roda
 * dentro de `map`. Sem isso seria preciso guardar "qual prato está sob o
 * dedo" num ref e reconstruir o gesto à mão.
 *
 * A FOTO É O CARD. Antes o prato aparecia numa miniatura redonda de 58px
 * — medida herdada do GymEats, onde o check-in é um registro. Aqui o
 * check-in É a foto: a nota, o palpite e a reação todos dependem de
 * alguém olhar o prato, e a miniatura escondia exatamente isso.
 *
 * O quadrado não é escolha estética: `prepararFotos` gera o thumb com
 * `quadrada: true`, então 1:1 é a proporção que o arquivo já tem. Pedir
 * 4:3 aqui cortaria um quarto de uma imagem que cabe inteira.
 */
function CardDoPrato(
  { cid, prato, minha, aoSegurar, semNotas }:
  { cid: string; prato: Plate; minha: string | null; aoSegurar: () => void; semNotas: boolean },
) {
  const segurar = useToqueLongo(aoSegurar);
  return (
    <Link className="prato-card" to={`/c/${cid}/p/${prato.id}`} {...segurar}>
      <div className="prato-foto">
        {prato.thumbPath
          ? <StoragePhoto path={prato.thumbPath} alt="" ratio="1 / 1" />
          : prato.semFoto && prato.description
            /* Postado sem foto: o quadrado mostra o que a pessoa comeu,
               que é o que substitui a foto em todo o resto do app. */
            ? <div className="foto prato-sem-foto texto" style={{ aspectRatio: "1 / 1" }}>
                <span aria-hidden="true">📝</span>
                <p>{prato.description}</p>
              </div>
            : <div className="foto prato-sem-foto" style={{ aspectRatio: "1 / 1" }}>
                <IconFork size={34} />
              </div>}
        {/* A nota fica na foto porque é o que faz a pessoa parar de rolar.
            Só aparece com nota: "—" num selo é ruído em prato recém-posto. */}
        {prato.ratingCount > 0 && !semNotas && !prato.fechado && (
          <span className="prato-selo nota">★ {formatRating(prato.ratingAvg)}</span>
        )}
        <span className="prato-selo hora">{hora(plateDate(prato))}</span>
      </div>

      <div className="prato-corpo">
        <div className="prato-titulo">{prato.title || "Sem título"}</div>
        <div className="prato-quem">
          <Avatar name={prato.authorName} photo={prato.authorPhoto} size="xs" />
          <span>{prato.authorName}</span>
        </div>

        {/* Rodapé de INFORMAÇÃO, não de ação: o card inteiro é um link, e
            um botão dentro de um link que na verdade só navega mente sobre
            o que vai acontecer. Dar nota continua sendo no prato aberto.

            Ele aparece SEMPRE, com os dois contadores mesmo em zero. A
            primeira versão escondia cada número quando era zero, e o
            resultado é que não dava pra distinguir "ninguém comentou" de
            "o card não mostra comentário" — a informação parecia sumida.
            Zero também é resposta, e o card ganha altura estável de
            quebra. Os contadores são do servidor (`commentCount` e
            `ratingCount`), mas prato antigo pode não ter o campo: `?? 0`
            porque `undefined` renderiza vazio e some com o número. */}
        <div className="prato-rodape">
          {/* O ícone é `aria-hidden` (vem assim do `Svg`), então sem rótulo
              o leitor de tela anunciaria só "5". */}
          <span className="prato-meta"
            aria-label={`${prato.ratingCount ?? 0} ${prato.ratingCount === 1 ? "nota" : "notas"}`}>
            <IconStar size={16} /> {prato.ratingCount ?? 0}
          </span>
          <span className="prato-meta"
            aria-label={`${prato.commentCount ?? 0} ${prato.commentCount === 1 ? "comentário" : "comentários"}`}>
            <IconChat size={16} /> {prato.commentCount ?? 0}
          </span>
          <span className="prato-espaco" />
          <ResumoDeReacoes reactions={prato.reactions} minha={minha} />
        </div>
      </div>
    </Link>
  );
}

const MESES = ["janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];

const rotuloDia = (dia: string): string => {
  const hoje = dayKey();
  if (dia === hoje) return "Hoje";
  const ontem = new Date();
  ontem.setDate(ontem.getDate() - 1);
  if (dia === dayKey(ontem)) return "Ontem";
  const [a, m, d] = dia.split("-").map(Number);
  const base = `${d} de ${MESES[(m ?? 1) - 1]}`;
  return a === new Date().getFullYear() ? base : `${base} de ${a}`;
};

const hora = (d: Date) => `${d.getHours()}:${String(d.getMinutes()).padStart(2, "0")}`;

/**
 * Dias seguidos com prato OU vale-falta, contando de hoje para trás.
 *
 * O coringa entra na conta porque é exatamente isso que ele faz no motor
 * de pontos (`scoreMember` junta os dois num conjunto só). Enquanto ele
 * não podia ser gasto isso não aparecia; agora que pode, uma tela que
 * ignorasse o dia coberto mostraria a sequência quebrada logo depois de
 * a pessoa pagar para consertá-la.
 *
 * Quem conta é `streakOn`, do core, e não um laço escrito aqui: era o
 * segundo laço de calendário desta tela, e dois jeitos de contar a mesma
 * sequência divergem no dia em que um deles ganha uma regra nova.
 */
function sequencia(dias: readonly string[], coringas: readonly string[] = []): number {
  const tem = new Set([...dias, ...coringas]);
  // Ainda não postou hoje não zera a sequência: o dia não acabou.
  const hoje = dayKey();
  return streakOn(tem, tem.has(hoje) ? hoje : previousDayKey(hoje));
}

/** Ontem, em DayKey. Mesma aritmética do core, do lado da tela. */
function previousDayKey(dia: string): string {
  const d = new Date(`${dia}T12:00:00`);
  d.setDate(d.getDate() - 1);
  return dayKey(d);
}

export function Challenge() {
  const { cid = "" } = useParams();
  const { user } = useSession();
  const [challenge, setChallenge] = useState<ChallengeDoc | null | undefined>(undefined);
  const [members, setMembers] = useState<Member[]>([]);
  const [plates, setPlates] = useState<Plate[] | null>(null);
  /* Qual card está com a barra aberta, e o que EU reagi em cada prato.
     O mapa acumula em vez de escutar todo prato do feed: uma assinatura
     por card seria uma consulta por card — justamente o custo que o
     resumo agregado no prato existe para evitar. */
  const bloqueados = useBloqueados();
  const [aberto, setAberto] = useState<string | null>(null);
  const [minhas, setMinhas] = useState<Record<string, string | null>>({});

  useEffect(() => watchChallenge(cid, setChallenge), [cid]);
  useEffect(() => watchMembers(cid, setMembers), [cid]);
  useEffect(() => watchFeed(cid, setPlates), [cid]);
  const vigente = useTemporadaVigente(cid);

  /* Só do prato aberto: é o único lugar onde saber a minha reação muda o
     que a tela desenha agora. */
  useEffect(() => {
    if (!aberto || !user) return;
    return watchMyReaction(cid, aberto, user.uid, (emoji) =>
      setMinhas((m) => ({ ...m, [aberto]: emoji })));
  }, [cid, aberto, user]);

  const escolher = useCallback(async (emoji: string) => {
    if (!aberto || !user) return;
    const atual = minhas[aberto] ?? null;
    setAberto(null);
    // A tela antecipa a própria reação; o resumo do card chega quando a
    // função devolver a contagem.
    setMinhas((m) => ({ ...m, [aberto]: atual === emoji ? null : emoji }));
    await reagir(cid, aberto, user.uid, emoji, atual);
  }, [cid, aberto, user, minhas]);

  // Um grupo por dia, na ordem em que os dias aparecem. Agrupar por
  // "iguais consecutivos" fazia "Hoje" e "Ontem" se repetirem na tela
  // sempre que um prato chegava fora de ordem.
  const dias = useMemo(() => {
    const mapa = new Map<string, Plate[]>();
    // Bloqueado some do feed. Esconder o perfil e deixar os pratos não é
    // bloqueio, é configuração — e é o que a loja recusa.
    for (const p of semBloqueados(plates ?? [], bloqueados)) {
      const lista = mapa.get(p.day);
      if (lista) lista.push(p); else mapa.set(p.day, [p]);
    }
    return [...mapa.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [plates, bloqueados]);

  const placar = useMemo(() => standings(members), [members]);
  const eu = placar.find((m) => m.uid === user?.uid);
  const lider = placar[0];

  if (challenge === undefined) return <div className="screen center"><Spinner /></div>;
  if (!challenge) {
    return (
      <main className="screen">
        <div className="topbar">
          <Link className="topbar-btn" to="/" aria-label="Voltar">←</Link>
          <div className="topbar-title" />
          <span className="topbar-btn" />
        </div>
        <Empty emoji="🤔" title="Você não faz parte deste desafio">
          <p>Você não faz mais parte dele, ou ele foi apagado.</p>
        </Empty>
      </main>
    );
  }

  const hoje = dayKey();
  const config: ChallengeConfig = resolveConfig(challenge);
  const pratosHoje = (plates ?? []).filter((p) => p.day === hoje).length;
  const minhaSequencia = eu ? sequencia(eu.days ?? [], eu.jokerDays ?? []) : 0;
  const minhaPosicao = eu ? placar.findIndex((m) => m.uid === eu.uid) + 1 : 0;

  /* ---------- a sequência precisa dizer quanto falta ----------

     Antes esta linha mostrava só `🔥 4`, e o bônus liga no sétimo dia:
     eram seis dias de esforço sem nenhum sinal de progresso e sem a
     pessoa saber que existia uma meta. Meta invisível não puxa ninguém —
     e o número que ela precisa é o que falta, não o que já tem.

     Depois que liga, o que interessa é o outro número: o multiplicador
     que ela está ganhando, porque é ele que dói de perder. */
  const sequenciaEmPalavras = (() => {
    if (!eu || minhaSequencia <= 0) return "";
    const meta = config.rules.streakFrom;
    if (minhaSequencia >= meta) {
      return ` · 🔥 ${minhaSequencia} · ${formatPoints(config.rules.streakMultiplier)}×`;
    }
    return ` · 🔥 ${minhaSequencia} de ${meta}`;
  })();

  /* A distância até quem lidera, em palavras. Antes a faixa mostrava dois
     totais lado a lado e cada um subtraía de cabeça — e o caso de empate,
     que é o mais interessante do grupo, não aparecia de jeito nenhum. */
  const recadoDoPlacar = (() => {
    if (!lider) return "Ninguém pontuou ainda";
    const primeiro = lider.name.split(" ")[0] ?? lider.name;
    if (!eu) return `${primeiro} lidera com ${formatPoints(lider.totalPoints)}`;
    const diferenca = lider.totalPoints - eu.totalPoints;
    if (eu.uid === lider.uid) {
      const segundo = placar[1];
      // Liderar sozinho e liderar empatado são situações diferentes, e o
      // grupo repara na diferença.
      if (!segundo || segundo.totalPoints === eu.totalPoints) return "Você lidera, empatado";
      return `Você lidera por ${formatPoints(eu.totalPoints - segundo.totalPoints)}`;
    }
    if (diferenca === 0) return `Você empatou com ${primeiro}`;
    return `${primeiro} está ${formatPoints(diferenca)} na frente`;
  })();

  return (
    <main className="screen">
      {/* A barra de cima do feed não tem título: o nome do desafio é o
          H1 grande logo abaixo, como no GymEats. */}
      {/* O nome do desafio subiu para a barra: ele repetia 60px de `<h1>` em
          toda rolagem, e o feed ganhou uma aba, então a seta de voltar leva
          para os desafios sem fingir que é um menu — o ícone de hambúrguer
          promete abrir alguma coisa e navegava um nível acima.

          A capa saiu daqui: ela não muda de um dia para o outro e empurrava
          o primeiro prato para 640px de rolagem. Passou a morar em Detalhes,
          que é onde alguém procura o que o desafio é. */}
      {/* Dois botões de cada lado, e o da esquerda é um vazio de propósito.

          O bate-papo perdeu a aba para o mapa e virou este ícone, ao lado
          do raio do recap. Só que o título desta barra é `flex: 1` com
          texto centralizado: ele se centraliza no espaço QUE SOBRA, então
          um botão a mais à direita empurraria o nome do desafio 24px para
          a esquerda do centro de verdade. Em nome longo ninguém nota; em
          nome curto fica torto, e torto de pouco é o que parece defeito.

          O vazio faz os dois lados medirem 80px, e aí o centro do título
          é o centro da tela. Os dois lados vão dentro de `.topbar-acoes`
          para que o `gap` da barra conte uma vez de cada lado — sem isso
          sobra a diferença de 8px, que é exatamente o tipo de desvio que
          ninguém consegue apontar mas todo mundo enxerga. */}
      <div className="topbar">
        <span className="topbar-acoes">
          <Link className="topbar-btn" to="/" aria-label="Meus desafios"><IconBack /></Link>
          <span className="topbar-btn" />
        </span>
        <div className="topbar-title">{challenge.name}</div>
        <span className="topbar-acoes">
          <Link className="topbar-btn" to={`/c/${cid}/bate-papo`} aria-label="Bate-papo">
            <IconChat />
          </Link>
          <Link className="topbar-btn" to={`/c/${cid}/recap`} aria-label="Recap"><IconBolt /></Link>
        </span>
      </div>

      <div className="screen-body">
        {/* Uma linha, não duas células com dois totais soltos: a distância
            até quem lidera é o número que muda comportamento, e antes cada
            um tinha que subtrair de cabeça. */}
        <Link className="placar-linha" to={`/c/${cid}/classificacao`}>
          {lider && <Avatar name={lider.name} photo={lider.photo} size="md" />}
          <div className="placar-txt">
            <div className="placar-t">{recadoDoPlacar}</div>
            <div className="placar-s">
              {minhaPosicao > 0 && `Você é ${minhaPosicao}º de ${placar.length} · `}
              {pratosHoje} {pratosHoje === 1 ? "prato hoje" : "pratos hoje"}
              {sequenciaEmPalavras}
            </div>
          </div>
          {eu && <div className="placar-n">{formatPoints(eu.totalPoints)}</div>}
          <span className="placar-chev"><IconChevron size={18} /></span>
        </Link>

        <ChegouOResumo cid={cid} startDay={challenge.startDay ?? ""} />

        <CartaoDoCampeao cid={cid} vigente={vigente} dispensavel
          fotos={Object.fromEntries(members.map((m) => [m.uid, m.photo]))} />

        {/* Antes do anúncio e depois do placar: é a primeira coisa que a
            pessoa vê ao abrir o desafio na manhã seguinte a uma falta, e
            some sozinho assim que não há falta a cobrir. */}
        {eu && (
          <ValeFalta cid={cid} config={config} eu={eu} hoje={hoje}
            startDay={challenge.startDay} endDay={challenge.endDay ?? ""} />
        )}

        {user && (
          <Cutucar cid={cid} eu={user.uid} membros={members} hoje={hoje} bloqueados={bloqueados} />
        )}

        {/* As missões valendo, com quanto falta. Some sem missão aberta. */}
        <FaixaDeMissoes cid={cid} config={config} eu={eu} hoje={hoje} />

        <Anuncio posicao="feed" />

        {plates === null ? <EsqueletoDoFeed /> : dias.length === 0 ? (
          <Empty emoji="📸" title="Ninguém postou ainda">
            <p>Toca no + e manda a foto do seu prato de hoje.</p>
          </Empty>
        ) : (
          dias.map(([dia, doDia]) => (
            <section key={dia}>
              <div className="section-label">{rotuloDia(dia)}</div>
              {doDia.map((p) => (
                <CardDoPrato key={p.id} cid={cid} prato={p} semNotas={interacaoDe(challenge) === "ninguem"}
                  minha={minhas[p.id] ?? null}
                  aoSegurar={() => setAberto(p.id)} />
              ))}
            </section>
          ))
        )}
      </div>

      {aberto && (
        <BarraDeReacao
          minha={minhas[aberto] ?? null}
          aoEscolher={(e) => void escolher(e)}
          aoFechar={() => setAberto(null)}
        />
      )}

      <Link className="fab" to={`/c/${cid}/novo`} aria-label="Publicar prato">
        <IconPlus size={30} />
      </Link>
      <TabBar cid={cid} />
    </main>
  );
}
